import { Component, Inject, Input, OnInit, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import {
    ApplicationFacade,
    JourneyPlannerItineraryTransportMode,
    Leg,
    OpenTripPlannerService,
    OtpUtil,
    PlannerStop,
    ShapeType,
    TripDetailsTime
} from '@ridango/components';
import { IonAccordionGroup, ModalController } from '@ionic/angular';
import { take, takeUntil } from 'rxjs/operators';
import { DisruptionMessageDetailsComponent } from '../../disruption-message-details/disruption-message-details.component';
import { BaseComponent, DisruptionMessage, DisruptionMessageType, matchesStopId } from '@ridango/core';
import Env from '@ridango/core/env';
import { AnalyticsService } from '@ridango/core/lib/services/analytics.service';
import { TrackedEventCategory } from '@ridango/core/lib/enums/analytics/tracked-event-categories.enum';
import { TrackedEventAction } from '@ridango/core/lib/enums/analytics/tracked-event-action.enum';

@Component({
    selector: 'rwidget-leg-trip',
    templateUrl: './leg-trip.component.html',
    styleUrls: ['./leg-trip.component.scss']
})
export class LegTripComponent extends BaseComponent implements OnInit, OnChanges {
    @Input() leg: Leg;
    @Input() originalLeg: Leg;
    @Input() tripSteps: TripDetailsTime[] = [];
    @Input() expandedByA11y: boolean;

    @ViewChild('middleTripSteps', { static: true }) accordionGroup: IonAccordionGroup;

    assetsPath$ = this.applicationFacade.assetsPath$;
    otpUtil = OtpUtil;
    ShapeType = ShapeType;
    legMode = JourneyPlannerItineraryTransportMode;
    routeUrl = '';
    isContrast$ = this.applicationFacade.highContrast$;
    isStopsExpanded = false;

    displayStartTime: number;
    crossedOutStartTime: number;
    isDepartureApproximateTime: boolean;
    displayEndTime: number;
    crossedOutEndTime: number;
    IsArrivalApproximateTime: boolean;
    thresholdMinutes = this.env.FIXED_TIME_THRESHOLD_MINUTES ?? 0;
    messagesForCorrespondingStopByFrom: DisruptionMessage[] = [];
    messagesForCorrespondingStopByTo: DisruptionMessage[] = [];
    messagesForCorrespondingRoute: DisruptionMessage[] = [];
    messagesForCorrespondingIntermediateStops: {
        [key in NonNullable<PlannerStop['stopId']> | NonNullable<PlannerStop['stopCode']>]: DisruptionMessage[];
    } = {};

    constructor(
        private applicationFacade: ApplicationFacade,
        @Inject('ENV') public env: Env,
        private modalCtrl: ModalController,
        private otpService: OpenTripPlannerService,
        private analyticsService: AnalyticsService
    ) {
        super();
    }

    stopDisruptionMessages: DisruptionMessage[];
    busDisruptionMessages: DisruptionMessage[];
    isTripArrivalDelayed: boolean;
    isTripDepartureDelayed: boolean;
    footnotes: DisruptionMessage[];
    operatorLogosBaseUrl: string;
    operatorLogoFileExists = true;
    private readonly TRIP_STOPS_EXPANDED_KEY = 'journeyPlanLegStops';
    private readonly SHOW_HIDE_BUTTON_ANALYTICS_NAME = 'Show/Hide stops';

    ngOnInit(): void {
        this.operatorLogosBaseUrl = `${this.env.STATIC_CONTENT_BASE_URL}/${this.env.APP_NAME}/operator_logos/`;

        this.footnotes = (this.leg as any)?.alerts?.filter((alert) => alert.effect === 'INFORMATION');
        this.isTripDepartureDelayed = this.leg.departureDelay !== 0;
        this.isTripArrivalDelayed = this.leg.arrivalDelay !== 0;

        if (this.env.JOURNEY_PLANNER?.SHOW_DISRUPTION_MESSAGES) {
            const disruptionMessages = this.leg.disruptionMessages?.filter((message) =>
                message.informedEntities?.some((entity) => {
                    if (entity?.tripId) {
                        return entity.tripId === this.leg?.tripId;
                    }

                    return [...this.leg.intermediateStops, this.leg.from, this.leg.to].some((stop) => {
                        if (entity.routeId?.toString() === this.leg.routeId) {
                            return true;
                        }

                        return matchesStopId(entity.stopId, stop.stopId, stop.stopCode);
                    });
                })
            );

            this.busDisruptionMessages = disruptionMessages?.filter(
                (message) =>
                    message.messageType === DisruptionMessageType.ALERT_BUS ||
                    message.messageType === 'ALERT_BUS_AND_STOP'
            );

            this.stopDisruptionMessages = disruptionMessages?.filter(
                (message) =>
                    message.messageType === DisruptionMessageType.ALERT_STOP ||
                    message.messageType === 'ALERT_BUS_AND_STOP'
            );
            this.preFilterDisruptionMessages();

            if (this.env.DIRECTIONAL_ALERTS) {
                this.removeNonDirectionalStopAlerts();
                this.removeNonDirectionalBusAlerts();
            }
        }

        if (this.env.JOURNEY_PLANNER?.SHOW_FARE_URL_ITINERARY_INSTRUCTIONS && this.leg.routeId) {
            this.getRouteDetails(this.leg.routeId);
        }

        this.getLegPrice();
        this.prepareDisplayTimes();
    }
    ngOnChanges(changes: SimpleChanges): void {
        if (changes.expandedByA11y && !changes.expandedByA11y.firstChange) {
            this.syncAccordionToA11yExpansion(!!changes.expandedByA11y.currentValue);
        }
        if (this.env.JOURNEY_PLANNER?.SHOW_DISRUPTION_MESSAGES) {
            this.preFilterDisruptionMessages();
        }
    }

    private syncAccordionToA11yExpansion(expanded: boolean): void {
        this.isStopsExpanded = expanded;
        this.accordionGroup.value = expanded ? this.TRIP_STOPS_EXPANDED_KEY : undefined;
    }

    private preFilterDisruptionMessages() {
        this.messagesForCorrespondingStopByFrom = this.getMessageForCorrespondingStop(this.leg?.from);
        this.messagesForCorrespondingStopByTo = this.getMessageForCorrespondingStop(this.leg?.to);
        this.messagesForCorrespondingRoute = this.getMessageForCorrespondingRoute(this.leg?.routeId);

        this.leg?.intermediateStops?.forEach((stopTime) => {
            const stopId = stopTime.stopId || stopTime.stopCode;
            if (!stopId) return;

            this.messagesForCorrespondingIntermediateStops[stopId] = this.getMessageForCorrespondingStop(stopTime);
        });
    }
    private prepareDisplayTimes(): void {
        const { realTime, depIsTimingPoint, arrIsTimingPoint, departureDelay, arrivalDelay } = this.leg;

        const { startTime, endTime } = this.originalLeg;

        this.displayStartTime = realTime && departureDelay != null ? startTime + departureDelay * 1000 : startTime;
        this.crossedOutStartTime = null;
        this.displayEndTime = realTime && arrivalDelay != null ? endTime + arrivalDelay * 1000 : endTime;
        this.crossedOutEndTime = null;
        this.isDepartureApproximateTime = false;
        this.IsArrivalApproximateTime = false;

        if (!realTime) {
            return;
        }
        if (departureDelay != null) {
            const isFixed = depIsTimingPoint;
            let thresholdMinutes: number;

            if (this.env.TIMEPOINTS_ENABLED) {
                this.isDepartureApproximateTime = !isFixed;
                thresholdMinutes = isFixed ? this.thresholdMinutes : this.env.APPROXIMATE_TIME_THRESHOLD_MINUTES;
            } else {
                this.isDepartureApproximateTime = false;
                thresholdMinutes = this.thresholdMinutes;
            }

            const delayMinutes = Math.floor(Math.abs(departureDelay / 60));
            const areMinutesDifferent =
                Math.floor(new Date(startTime).getTime() / 60000) !==
                Math.floor(new Date(this.displayStartTime).getTime() / 60000);

            if (delayMinutes >= thresholdMinutes && areMinutesDifferent) {
                this.crossedOutStartTime = startTime;
            }
        }

        if (arrivalDelay != null) {
            const isFixed = arrIsTimingPoint;
            let thresholdMinutes: number;

            if (this.env.TIMEPOINTS_ENABLED) {
                this.IsArrivalApproximateTime = !isFixed;
                thresholdMinutes = isFixed ? this.thresholdMinutes : this.env.APPROXIMATE_TIME_THRESHOLD_MINUTES;
            } else {
                this.IsArrivalApproximateTime = false;
                thresholdMinutes = this.thresholdMinutes;
            }

            const delayMinutes = Math.floor(Math.abs(arrivalDelay / 60));
            const areMinutesDifferent =
                Math.floor(new Date(endTime).getTime() / 60000) !==
                Math.floor(new Date(this.displayEndTime).getTime() / 60000);

            if (delayMinutes >= thresholdMinutes && areMinutesDifferent) {
                this.crossedOutEndTime = endTime;
            }
        }
    }

    /** Filter out alerts that are not relevant to the trip direction
     *  only applies if setting DIRECTIONAL_ALERTS is set to true in the environment and
     *  the alert has a tripDirection property set
     * */
    private filterDirectionalAlerts(
        messages: DisruptionMessage[],
        tripDirection: string | null,
        routeId: string | null,
        messageTypes: DisruptionMessageType[]
    ): any[] {
        return messages
            .map((message) => {
                const filteredEntities = message.informedEntities?.filter((entity) => {
                    const alertDirection = entity?.tripDirection?.toString();
                    const alertRouteId = entity?.routeId?.toString();

                    if ((!tripDirection || !alertDirection) && messageTypes.includes(message.messageType)) {
                        return true;
                    }
                    return (
                        alertDirection === tripDirection &&
                        alertRouteId === routeId &&
                        messageTypes.includes(message.messageType)
                    );
                });

                if (filteredEntities?.length > 0) {
                    return { ...message, informedEntities: filteredEntities };
                } else {
                    return null;
                }
            })
            .filter((message) => message !== null);
    }

    private removeNonDirectionalStopAlerts(): void {
        if (this.stopDisruptionMessages) {
            this.stopDisruptionMessages = this.filterDirectionalAlerts(
                this.stopDisruptionMessages,
                this.leg.tripDirection?.toString(),
                this.leg.routeId?.toString(),
                [DisruptionMessageType.ALERT_STOP, DisruptionMessageType.ALERT_BUS_AND_STOP]
            );
        }
    }

    private removeNonDirectionalBusAlerts(): void {
        if (this.busDisruptionMessages) {
            this.busDisruptionMessages = this.filterDirectionalAlerts(
                this.busDisruptionMessages,
                this.leg.tripDirection?.toString(),
                this.leg.routeId?.toString(),
                [DisruptionMessageType.ALERT_BUS, DisruptionMessageType.ALERT_BUS_AND_STOP]
            );
        }
    }

    getMessageForCorrespondingStop(stop: PlannerStop) {
        return this.stopDisruptionMessages?.filter((message) =>
            message.informedEntities?.some((entity) => matchesStopId(entity.stopId, stop.stopId, stop.stopCode))
        );
    }

    getMessageForCorrespondingRoute(routeId?: string) {
        return this.stopDisruptionMessages?.filter((message) =>
            message.informedEntities?.some((entity) => entity.routeId?.toString() === routeId)
        );
    }

    getRouteDetails(routeId: string) {
        this.otpService
            .getRoute(routeId)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((route) => {
                this.routeUrl = route.url;
            });
    }

    getLegPrice(): void {
        if (!this.env.JOURNEY_PLANNER?.SHOW_LEG_PRICES) {
            return;
        }

        const { routeId, from, to, routeShortName } = this.leg;
        if (!routeId || !from?.stopId || !to?.stopId || !routeShortName) {
            return;
        }

        this.otpService
            .getJourneyLegPrice(routeId, from.stopId, to.stopId, routeShortName)
            .pipe(take(1))
            .subscribe(
                (response) => {
                    this.leg = { ...this.leg, price: response.amount, currency: response.currency };
                },
                (error) => {
                    console.error('Failed to get leg price:', error);
                }
            );
    }

    getOriginPath(color: string | undefined) {
        let resolved = this.otpUtil.resolveColor(color);
        this.isContrast$.pipe(take(1)).subscribe((isContrast) => {
            if (isContrast) resolved = '#ffff00';
        });
        return {
            background: `linear-gradient(transparent 0%, transparent 12px, ${resolved} 12px, ${resolved} 100%)`
        };
    }

    getDestinationPath(color: string | undefined) {
        let resolved = this.otpUtil.resolveColor(color);
        this.isContrast$.pipe(take(1)).subscribe((isContrast) => {
            if (isContrast) resolved = '#ffff00';
        });
        return {
            background: `linear-gradient(${resolved} 0%, ${resolved} 70%, transparent 50%, transparent 100%)`
        };
    }

    getFullPath(color: string | undefined) {
        let resolved = this.otpUtil.resolveColor(color);
        this.isContrast$.pipe(take(1)).subscribe((isContrast) => {
            if (isContrast) resolved = '#ffff00';
        });
        return { background: `linear-gradient(${resolved} 0%, ${resolved} 100%)` };
    }

    toggleExpandCollapseTripStops() {
        this.analyticsService.eventTrack(TrackedEventAction.CLICKED, {
            category: TrackedEventCategory.JOURNEY_PLANNER,
            name: this.SHOW_HIDE_BUTTON_ANALYTICS_NAME
        });
        const nativeEl = this.accordionGroup;
        this.isStopsExpanded = !this.isToggleExpandedTripStops();

        if (this.isToggleExpandedTripStops()) {
            nativeEl.value = undefined;
        } else {
            nativeEl.value = this.TRIP_STOPS_EXPANDED_KEY;
        }
    }

    isToggleExpandedTripStops() {
        return this.accordionGroup.value === this.TRIP_STOPS_EXPANDED_KEY;
    }

    async openDisruptionMessageDetailsView(message: DisruptionMessage) {
        const modal = await this.modalCtrl.create({
            component: DisruptionMessageDetailsComponent,
            componentProps: {
                disruptionMessage: message,
                leg: this.leg
            }
        });

        modal.componentProps = {
            ...modal.componentProps,
            modalRef: modal
        };

        await modal.present();
    }
}
